import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FiChevronDown, FiChevronRight, FiFolder, FiLoader } from "react-icons/fi";
import {
  GetFolderPathDocument,
  type GetFolderPathQuery,
  type GetFolderPathQueryVariables,
  GetFoldersDocument,
  type GetFoldersQuery,
  type GetFoldersQueryVariables,
  type MediaType,
  SearchFoldersDocument,
  type SearchFoldersQuery,
  type SearchFoldersQueryVariables,
} from "../../../generated/graphql";
import { useAppDispatch, useAppSelector } from "../../../store";
import { navigateToFolder } from "../../../store/slices/mediaSlice";
import { ContextMenu } from "../ContextMenu";
import { SortDropdown, type SortOption } from "../layout/SortDropdown";
import { ConfirmDeleteModal } from "../modals/ConfirmDeleteModal";
import { CreateFolderModal } from "../modals/CreateFolderModal";
import { RenameFolderModal } from "../modals/RenameFolderModal";
import { FolderCard } from "./FolderCard";

interface FoldersSectionProps {
  parentId: string | null;
  mediaType: MediaType;
  searchQuery?: string;
  onClearSearch?: () => void;
}

export function FoldersSection({
  parentId,
  mediaType,
  searchQuery = "",
  onClearSearch,
}: FoldersSectionProps) {
  const dispatch = useAppDispatch();
  const viewMode = useAppSelector((state) => state.media.viewMode);
  const [isExpanded, setIsExpanded] = useState(true);
  const [sortOption, setSortOption] = useState<SortOption>("newest");

  // Folder type from edges
  type FolderNode = NonNullable<GetFoldersQuery["folders"]["edges"][0]>["node"];

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    folder: FolderNode | null;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
    folder: null,
  });

  // Modal states
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] = useState(false);
  const [selectedParentFolder, setSelectedParentFolder] = useState<string | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFolderData, setRenameFolderData] = useState<{ id: string; name: string } | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFolderData, setDeleteFolderData] = useState<{
    id: string;
    name: string;
    parentId: string | null;
  } | null>(null);
  const [navigatingFolderId, setNavigatingFolderId] = useState<string | null>(null);

  const isSearching = searchQuery.trim().length > 0;

  // Query child folders (when not searching)
  const { data: foldersData, loading: foldersLoading } = useQuery<
    GetFoldersQuery,
    GetFoldersQueryVariables
  >(GetFoldersDocument, {
    variables: { parentId, type: mediaType },
    skip: isSearching,
  });

  // Query search results (when searching)
  const { data: searchData, loading: searchLoading } = useQuery<
    SearchFoldersQuery,
    SearchFoldersQueryVariables
  >(SearchFoldersDocument, {
    variables: { query: searchQuery, type: mediaType },
    skip: !isSearching,
  });

  // Lazy query to get folder path when navigating from search
  const [getFolderPath] = useLazyQuery<GetFolderPathQuery, GetFolderPathQueryVariables>(
    GetFolderPathDocument
  );

  const loading = isSearching ? searchLoading : foldersLoading;

  // Use search results when searching, otherwise use folder results (extracted from edges)
  const allFolders = isSearching
    ? searchData?.searchFolders || []
    : foldersData?.folders?.edges?.map((e) => e.node) || [];

  // Sort folders based on sortOption
  const folders = [...allFolders].sort((a, b) => {
    switch (sortOption) {
      case "newest":
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case "oldest":
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
          <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  if (folders.length === 0) {
    return null; // Don't show section if no folders
  }

  const handleFolderClick = async (folder: FolderNode) => {
    if (isSearching) {
      // Show loading state while fetching path
      setNavigatingFolderId(folder.id);

      try {
        // Fetch the full path when navigating from search
        const { data } = await getFolderPath({ variables: { id: folder.id } });
        const pathFolders = data?.folderPath || [];

        // Build parentPath from ancestors (excluding the target folder itself)
        const parentPath = pathFolders.slice(0, -1).map((f) => ({ id: f.id, name: f.name }));

        // Clear search and navigate together to avoid double query
        onClearSearch?.();
        dispatch(
          navigateToFolder({
            folderId: folder.id,
            folderName: folder.name,
            parentPath,
          })
        );
      } finally {
        setNavigatingFolderId(null);
      }
    } else {
      dispatch(
        navigateToFolder({
          folderId: folder.id,
          folderName: folder.name,
        })
      );
    }
  };

  const handleFolderContextMenu = (e: React.MouseEvent, folder: FolderNode) => {
    e.preventDefault();
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      folder,
    });
  };

  const handleContextMenuAction = (action: string) => {
    if (!contextMenu.folder) return;

    switch (action) {
      case "new-subfolder":
        setSelectedParentFolder(contextMenu.folder.id);
        setShowCreateSubfolderModal(true);
        break;
      case "rename":
        setRenameFolderData({
          id: contextMenu.folder.id,
          name: contextMenu.folder.name,
        });
        setShowRenameModal(true);
        break;
      case "delete":
        setDeleteFolderData({
          id: contextMenu.folder.id,
          name: contextMenu.folder.name,
          parentId: contextMenu.folder.parent ?? null,
        });
        setShowDeleteModal(true);
        break;
    }
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 }, folder: null });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 }, folder: null });
  };

  return (
    <>
      <div className="rounded-lg border border-gray-200 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-left hover:bg-gray-50 transition-colors rounded-md px-2 py-1 -ml-2"
          >
            {isExpanded ? (
              <FiChevronDown className="h-5 w-5 text-gray-500" />
            ) : (
              <FiChevronRight className="h-5 w-5 text-gray-500" />
            )}
            <h3 className="text-sm font-semibold text-gray-900">Folders ({folders.length})</h3>
          </button>
          <SortDropdown value={sortOption} onChange={setSortOption} />
        </div>

        {/* Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4">
            {viewMode === "grid" ? (
              // Grid View
              <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {folders.map((folder) => (
                  <FolderCard
                    key={folder.id}
                    folder={folder}
                    isLoading={navigatingFolderId === folder.id}
                    onClick={() => handleFolderClick(folder)}
                    onContextMenu={(e) => handleFolderContextMenu(e, folder)}
                  />
                ))}
              </div>
            ) : (
              // List View
              <div className="space-y-1">
                {folders.map((folder) => {
                  const isLoading = navigatingFolderId === folder.id;
                  return (
                    <button
                      key={folder.id}
                      type="button"
                      onClick={() => handleFolderClick(folder)}
                      onContextMenu={(e) => handleFolderContextMenu(e, folder)}
                      disabled={isLoading}
                      className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-gray-100 transition-colors ${
                        isLoading ? "opacity-60" : ""
                      }`}
                    >
                      {isLoading ? (
                        <FiLoader className="h-5 w-5 flex-shrink-0 text-blue-500 animate-spin" />
                      ) : (
                        <FiFolder className="h-5 w-5 flex-shrink-0 text-blue-500" />
                      )}
                      <span className="flex-1 truncate text-sm font-medium text-gray-900">
                        {folder.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* TODO: Add "Load more" button for pagination if folders.length > 8 */}
          </div>
        )}
      </div>

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        itemType="folder"
        item={
          contextMenu.folder
            ? {
                id: contextMenu.folder.id,
              }
            : undefined
        }
        onAction={handleContextMenuAction}
        onClose={handleCloseContextMenu}
      />

      {/* Create Subfolder Modal */}
      <CreateFolderModal
        isOpen={showCreateSubfolderModal}
        onClose={() => {
          setShowCreateSubfolderModal(false);
          setSelectedParentFolder(null);
        }}
        parentId={selectedParentFolder}
        type={mediaType}
      />

      {/* Rename Folder Modal */}
      {renameFolderData && (
        <RenameFolderModal
          isOpen={showRenameModal}
          onClose={() => {
            setShowRenameModal(false);
            setRenameFolderData(null);
          }}
          folderId={renameFolderData.id}
          currentName={renameFolderData.name}
        />
      )}

      {/* Delete Folder Modal */}
      {deleteFolderData && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteFolderData(null);
          }}
          type="folder"
          itemIds={[deleteFolderData.id]}
          itemName={deleteFolderData.name}
          parentId={deleteFolderData.parentId}
          mediaType={mediaType}
        />
      )}
    </>
  );
}
